import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { CancelDealerRequest, CancelDealerResponse, CreateEditSubAccountData, SubAccount } from './sub-accounts.types';
import {
  TransportCatalogOption,
  TransportRuleSet,
  TransportRuleSetPayload,
} from './transport-types-editor/transport-types.types';

@Injectable({ providedIn: 'root' })
export class SubAccountsService {
  // Private
  private _subAccounts = new BehaviorSubject<SubAccount[]>([]);
  private _subAccountsPlatform = new BehaviorSubject<SubAccount[]>([]);
  private readonly _httpClient = inject(HttpClient);

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get Sub-Accounts
   */
  getSubAccounts(page: number = 1, limit: number = 500): Observable<SubAccount[]> {
    const params = new HttpParams().set('page', String(page)).set('limit', String(limit));

    return this._httpClient.get<SubAccount[]>(`${environment.engineUrl}/sub-account`, { params }).pipe(
      map((response) => {
        this._subAccounts.next(response);
        return response;
      })
    );
  }

  /**
   * get Sub-Accounts By Account
   */
  getSubAccountsByAccount(accountUID: string): Observable<SubAccount[]> {
    const params = new HttpParams().set('accountUID', accountUID).set('page', '1').set('limit', '500');

    return this._httpClient.get<SubAccount[]>(`${environment.engineUrl}/sub-account`, { params }).pipe(
      map((response) => {
        this._subAccounts.next(response);
        return response;
      })
    );
  }

  getAllSubAccountsByAccount(accountUuid: string): Observable<SubAccount[]> {
    return this._httpClient.get<SubAccount[]>(`${environment.platformUrl}/accounts/${accountUuid}/subaccounts`).pipe(
      map((response) => {
        this._subAccountsPlatform.next(response);
        return response;
      })
    );
  }

  getSubAccontsLocal(): Observable<SubAccount[]> {
    return this._subAccounts.asObservable();
  }

  /**
   * Get a specific Sub-Account by ID
   * @param subAccountId - ID of the sub-account
   */
  getSubAccountById(subAccountId: string): Observable<SubAccount> {
    return this._httpClient.get<SubAccount>(`${environment.engineUrl}/sub-account/${subAccountId}`);
  }

  /**
   * Create a new sub-accounts
   * @param subAccount
   */
  createSubAccounts(subAccount: CreateEditSubAccountData): Observable<CreateEditSubAccountData> {
    return this._httpClient.post<CreateEditSubAccountData>(`${environment.engineUrl}/sub-account`, subAccount).pipe(
      map((response) => {
        return response;
      })
    );
  }

  /**
   * Edit Sub Accounts
   * @param subAccountsData
   * @param uid
   */
  editSubAccounts(uid: string, subAccountsData: CreateEditSubAccountData): Observable<CreateEditSubAccountData> {
    return this._httpClient
      .put<CreateEditSubAccountData>(`${environment.engineUrl}/sub-account/${uid}`, subAccountsData)
      .pipe(
        map((response) => {
          const subAccounts = this._subAccounts.getValue();
          const index = subAccounts.findIndex(subAccount => subAccount._id === uid);
          subAccounts[index] = { ...subAccounts[index], ...subAccountsData };
          this._subAccounts.next([...subAccounts]);
          return response;
        })
      );
  }

  /**
   * Schedule dealer cancellation via ai-engine (mirrors Platform lifecycle).
   * Uses localEngineUrl when configured (local dev); other sub-account calls use engineUrl.
   */
  cancelDealer(_accountUID: string, subAccountUID: string, body: CancelDealerRequest): Observable<CancelDealerResponse> {
    return this._httpClient.post<CancelDealerResponse>(`${environment.engineUrl}/sub-account/${subAccountUID}/cancel`, body);
  }

  /**
   * AIVA-2077: the dealer's transport rules. A dealer with nothing saved comes
   * back as `{ subAccountUID, defaultTypes: [], rules: [] }` (all options).
   */
  getTransportRules(subAccountUID: string): Observable<TransportRuleSet> {
    return this._httpClient.get<TransportRuleSet>(`${environment.engineUrl}/transport-rules/${subAccountUID}`);
  }

  saveTransportRules(subAccountUID: string, payload: TransportRuleSetPayload): Observable<TransportRuleSet> {
    return this._httpClient.put<TransportRuleSet>(`${environment.engineUrl}/transport-rules/${subAccountUID}`, payload);
  }

  /** AIVA-2077: transport options the dealer's DMS offers (Drop Off, Waiter, ...). */
  getTransportCatalog(subAccountUID: string): Observable<TransportCatalogOption[]> {
    return this._httpClient
      .get<TransportCatalogOption[]>(`${environment.engineUrl}/transport-rules/${subAccountUID}/catalog`)
      .pipe(map(options => options ?? []));
  }

  applyCancelScheduled(subAccountUID: string, response: CancelDealerResponse, reason?: string): void {
    const subAccounts = this._subAccounts.getValue();
    const nextSubAccounts = subAccounts.map((subAccount) => {
      const uid = subAccount.subAccountUID ?? subAccount._id;
      if (uid !== subAccountUID) {
        return subAccount;
      }

      return {
        ...subAccount,
        status: response.status,
        subscriptionEndDate: response.subscriptionEndDate,
        cancelScheduledAt: new Date().toISOString(),
        cancellationReason: reason ?? subAccount.cancellationReason,
      };
    });

    this._subAccounts.next(nextSubAccounts);
  }
}
